import axios from "axios";
import querystring from "querystring";
import {
  BASE_URL_RELIANCE,
  getCookie,
  DOMAIN_COOKIE_NAME,
  DAARA_REFRESH_TOKEN,
  DAARA_COOKIE_NAME,
  ENROLLEE_GATEWAY_URL,
  getDomain,
  isEnrolleeGatewayEnabled,
} from "./utilityService";
import { isFeatureEnabled } from "@reliance/feature-flags";
import { features } from "./constants";
import Cookies from "js-cookie";
import Localization from "../utils/localization";

import { apiClient } from "./oidc/authConfig";
import { envVariables } from "../config/appConfig";

const BASE_URL = BASE_URL_RELIANCE;

export const ENDPOINTS = {
  base: `${BASE_URL}/enrollees`,
  doctor: {
    register: "doctors/profile",
    retrieveStatus: (doctorId) => `/doctors/${doctorId}/status`,
    utilities: "/utility/specialties",
  },
  feedbackThread: {
    postFeedback: "/enrollees/feedback",
    getFeedbackThreads: "/enrollees/feedback/thread",
    getFeedbackThreadById: (id) => `/enrollees/feedback/thread/${id}`,
    postFeedbackToThread: (id) => `/enrollees/feedback/thread/${id}`,
  },
  consultations: {
    consultation: "/consultations",
    consultationDetails: (id) => `/consultations/${id}`,
    status: (id) => `/consultations/${id}/status`,
    messages: (id) => `/consultations/${id}/messages`,
    prescriptions: (id, prescriptionId) =>
      `/consultations/${id}/prescriptions/${prescriptionId}/new`,
    prescriptionsPickup: (id, prescriptionId) =>
      `/consultations/${id}/prescriptions/${prescriptionId}`,
    prescriptionsDelivery: (consultationId, prescriptionId) =>
      `/consultations/${consultationId}/prescriptions/${prescriptionId}/delivery`,
    prescriptionsProvider: (id, prescriptionId) =>
      `/consultations/${id}/prescriptions/${prescriptionId}/provider`,
    complaints: "/consultations/complaints",
    referrals: (id, referralId) =>
      `consultations/${id}/referrals/${referralId}`,
    rating: (id) => `consultations/${id}/ratings`,
    lgas: `/utility/lgas`,
  },
  profile: {
    get profileDetails() {
      return "/enrollees/profile";
    },
  },
  pharmacies: {
    allPharmacies: "consultations/pharmacies",
  },
  careBalance: {
    activate: "/care-wallets",
    getWallet: (owner) => {
      return `/care-wallets/${owner}`;
    },
    profile: "/profile",
    fund: "/care-wallet-payment-links",
    careHistory: (walletId) => `/care-wallets/${walletId}/transactions`,
    withdraw: "/care-wallet-withdrawals",
    getBanks: "/utility/banks",
    subscription: "/subscription",
  },
};

export class ApiService {
  #daaraToken;
  #refreshToken;
  #data;
  accessToken;
  ax;
  gatewayAx;
  #staticAccessToken;

  constructor(accessToken = "", lang = "en-NG", BASE = BASE_URL) {
    this.#daaraToken = getCookie(DAARA_COOKIE_NAME);
    this.#refreshToken = getCookie(DAARA_REFRESH_TOKEN);
    this.#staticAccessToken = getCookie(DOMAIN_COOKIE_NAME);

    this.accessToken = accessToken
      ? accessToken
      : isFeatureEnabled(features.jwtToken)
        ? this.#daaraToken
        : this.#staticAccessToken;

    this.ax = axios.create({
      baseURL: BASE,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    if (isEnrolleeGatewayEnabled()) {
      this.gatewayAx = axios.create({
        baseURL: ENROLLEE_GATEWAY_URL,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
      });
      this.gatewayAx.interceptors.request.use((config) => {
        config.url = config.url?.replace(/^\/?enrollees\//, "/");
        config._isGatewayRequest = true;

        const freshToken = getCookie(DAARA_COOKIE_NAME);
        if (freshToken) {
          config.headers["Authorization"] = `Bearer ${freshToken}`;
        }
        return config;
      });
      this.#applyInterceptors(this.gatewayAx);
    }

    this.#applyInterceptors(this.ax);
  }

  #applyInterceptors(instance) {
    instance.interceptors.request.use(
      async (config) => {
        const lang = Cookies.get("i18next")
          ? Cookies.get("i18next").split("-")[0]
          : Localization.getPrimaryLocale().split("-")[0];
        config.params = config.params || {};
        config.params.lang = lang;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    instance.interceptors.response.use(
      (res) => res,
      this.handleAuthError.bind(this),
    );
  }

  #getClient(url) {
    if (this.gatewayAx && url?.includes("enrollees/")) {
      return this.gatewayAx;
    }
    return this.ax;
  }

  async handleAuthError(error) {
    const originalRequest = error.config;

    const pathsToIgnore = ["/accounts/fcm-token", "/enrollees/providers", "/providers"];
    const ignoredPath = pathsToIgnore.some((path) =>
      originalRequest.url?.includes(path),
    );

    if (ignoredPath) {
      return Promise.reject(error);
    }

    if (
      (error.response?.status === 401 || error?.code === "ERR_NETWORK") &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      
      try {
        const { accessToken, refreshToken } = await this.refreshAccessToken();
        this.accessToken = accessToken;

        // Update auth headers on both instances
        this.ax.defaults.headers["Authorization"] = `Bearer ${accessToken}`;
        if (this.gatewayAx) {
          this.gatewayAx.defaults.headers["Authorization"] =
            `Bearer ${accessToken}`;
        }

        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        Cookies.set(DAARA_COOKIE_NAME, accessToken, { domain: getDomain() });
        Cookies.set(DAARA_REFRESH_TOKEN, refreshToken, { domain: getDomain() });

        // Retry with the correct client — use the flag because the URL has
        // already been rewritten by the gateway interceptor at this point
        const client = originalRequest._isGatewayRequest
          ? this.gatewayAx
          : this.ax;
        return client(originalRequest);
      } catch (refreshError) {
        this.logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }

  async refreshAccessToken() {
    try {
      const { data } = await axios.get(
        `${ENROLLEE_GATEWAY_URL}/sessions/refresh`,
        {
          headers: {
            Authorization: this.#refreshToken,
          },
        },
      );

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
      };
    } catch (error) {
      throw new ApiServiceException(error);
    }
  }

  logout() {
    window.location.href = `/logout`;
  }

  async post(url, data) {
    try {
      const client = this.#getClient(url);
      const response = await client.post(url, data);
      return response.data;
    } catch (e) {
      throw new ApiServiceException(e);
    }
  }

  async put(url, data) {
    try {
      const client = this.#getClient(url);
      const response = await client.put(url, data);
      return response.data;
    } catch (e) {
      throw new ApiServiceException(e);
    }
  }

  async patch(url, data) {
    try {
      const client = this.#getClient(url);
      const response = await client.patch(url, data);
      return response.data;
    } catch (e) {
      throw new ApiServiceException(e);
    }
  }

  async get(url, data) {
    try {
      const client = this.#getClient(url);
      const response = await client.get(
        `${url}?${querystring.stringify(data)}`,
      );
      return response.data;
    } catch (e) {
      throw new ApiServiceException(e);
    }
  }
}

export class ApiServiceException extends Error {
  constructor(e, _meta = {}) {
    const defaultError = {
      response: {
        status: 0,
        statusText: "Unknown Error",
        data: { message: "An Error Occurred" },
      },
    };

    e = e.response ? e : defaultError;

    const message =
      e.response?.data?.message ||
      e.response?.statusText ||
      e.response?.message ||
      e?.message ||
      "An Error occurred";
    super(message);

    this.code = e?.code || e?.response?.status;
    this.status = e?.status || e?.response?.status;
    this.errorMessage = message;
    this.response = e?.response || e;

    this.meta = {
      ..._meta,
      url: e?.config?.url,
      baseURL: e?.config?.baseURL,
      params: { ...e?.config?.params },
      method: e?.config?.method,
      success: false,
      timestamp: new Date().toISOString(),
    };

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiServiceException);
    }
  }
}

export default envVariables.REACT_APP_FEATURE_ENABLE_KEYCLOAK_SSO
  ? apiClient
  : new ApiService();
